import React, { useState, useEffect } from 'react';
import {
  Settings,
  Percent,
  ShieldCheck,
  Lock,
  Clock,
  RefreshCw,
  Save,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { fetchAdminSettingsApi, updateAdminSettingsApi } from '../../../lib/adminService';

interface SettingsTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
  adminRole?: string;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({ onShowNotice, adminRole = 'SUPER_ADMIN' }) => {
  const [platformFeeBps, setPlatformFeeBps] = useState(1500); // 15%
  const [payoutHoldHours, setPayoutHoldHours] = useState(24);
  const [autoApprovePayouts, setAutoApprovePayouts] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isSuperAdmin = adminRole === 'SUPER_ADMIN';

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminSettingsApi();
      if (data) {
        setPlatformFeeBps(data.platformFeeBps ?? 1500);
        setPayoutHoldHours(data.payoutHoldHours ?? 24);
        setAutoApprovePayouts(Boolean(data.autoApprovePayouts));
      }
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load platform settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      onShowNotice('Only SUPER_ADMIN can modify system financial configurations', 'error');
      return;
    }

    setIsSaving(true);
    try {
      await updateAdminSettingsApi({
        platformFeeBps: Number(platformFeeBps),
        payoutHoldHours: Number(payoutHoldHours),
        autoApprovePayouts,
      });
      onShowNotice('Platform settings updated and audited successfully!', 'success');
    } catch (err: any) {
      onShowNotice(err.message || 'Error updating settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const currentPercent = (platformFeeBps / 100).toFixed(1);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-black text-white tracking-tight">System Configuration & Security</h2>
          <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs font-bold">
            Root Config
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Section 19 & 39: Platform fee BPS, companion payout hold periods, MFA enforcement, and operational thresholds.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Platform Fee Card (Section 19 & 39) */}
        <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-purple-500/20 text-purple-400">
                <Percent className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">PairUpToday Platform Fee Rate</h3>
                <p className="text-xs text-slate-400">
                  Calculated strictly server-side on every booking checkout.
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-black text-purple-400 font-mono">{currentPercent}%</span>
              <span className="text-[10px] text-slate-500 block font-mono">{platformFeeBps} BPS</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">
                Basis Points (BPS) <span className="text-slate-500 font-normal">(100 BPS = 1%)</span>
              </label>
              <input
                type="number"
                disabled={!isSuperAdmin}
                min={0}
                max={5000}
                step={50}
                value={platformFeeBps}
                onChange={(e) => setPlatformFeeBps(Number(e.target.value))}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm font-mono text-white focus:outline-hidden focus:border-purple-500 disabled:opacity-50"
              />
            </div>

            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-400 flex flex-col justify-center font-mono">
              <span>Gross ₹1,000 Booking:</span>
              <span className="text-purple-300 font-bold mt-1">
                Platform Fee: ₹{(1000 * (platformFeeBps / 10000)).toFixed(0)} • Companion Payout: ₹{(1000 - (1000 * (platformFeeBps / 10000))).toFixed(0)}
              </span>
            </div>
          </div>
        </div>

        {/* Payout Security & Hold Hours */}
        <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-500/20 text-blue-400">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">Payout Settlement Hold Period</h3>
              <p className="text-xs text-slate-400">
                Minimum dispute buffer duration after meeting OTP verification before releasing payout.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">Hold Duration (Hours)</label>
              <input
                type="number"
                disabled={!isSuperAdmin}
                min={0}
                max={168}
                value={payoutHoldHours}
                onChange={(e) => setPayoutHoldHours(Number(e.target.value))}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm font-mono text-white focus:outline-hidden focus:border-purple-500 disabled:opacity-50"
              />
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <input
                type="checkbox"
                id="autoApprovePayouts"
                disabled={!isSuperAdmin}
                checked={autoApprovePayouts}
                onChange={(e) => setAutoApprovePayouts(e.target.checked)}
                className="w-4 h-4 rounded border-slate-800 text-purple-600 focus:ring-purple-500 bg-slate-900 cursor-pointer disabled:opacity-50"
              />
              <label htmlFor="autoApprovePayouts" className="text-xs text-slate-300 cursor-pointer">
                <strong>Automated Payout Release</strong>
                <span className="block text-[11px] text-slate-500">Automatically release net payout after hold window expires without dispute.</span>
              </label>
            </div>
          </div>
        </div>

        {/* SuperAdmin Save Button */}
        {isSuperAdmin ? (
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold rounded-xl text-xs flex items-center gap-2 cursor-pointer shadow-lg shadow-purple-950 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Recording Changes...' : 'Save & Audit Platform Settings'}</span>
            </button>
          </div>
        ) : (
          <div className="p-3 rounded-xl bg-amber-950/40 border border-amber-800/50 text-xs text-amber-300 flex items-center gap-2 font-mono">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Read-only configuration view. Role {adminRole} cannot mutate platform settings.</span>
          </div>
        )}
      </form>
    </div>
  );
};
